import { Button, ButtonGroup, Col, FormFeedback, Input, Label, Row } from "reactstrap"
import CreatableSelect from "react-select/creatable"
import { formatBDT } from "utils/formatPrice"
import { convertToWords } from "helpers/number_helpers"

export const AC_HP_FORMAT_OPTIONS = ["H/P", "A/C"]
export const DEFAULT_AC_HP_FORMAT = "H/P"

export const LOAN_FIELD_CONFIG = [
  { name: "bank_name", label: "Bank Name", type: "text", required: true },
  {
    name: "branch_name",
    label: "Branch Name/Address",
    type: "text",
    required: true,
  },
  { name: "unit_price", label: "Unit Price", type: "number", required: true },
  { name: "bank_paid", label: "Bank Paid", type: "number", required: true },
  {
    name: "bank_payment_mode",
    label: "Bank Payment Mode",
    type: "payment_method_select",
    required: false,
  },
  {
    name: "bank_payment_remarks",
    label: "Bank Payment Remarks",
    type: "textarea",
    required: false,
  },
  {
    name: "customer_paid",
    label: "Customer Paid",
    type: "number",
    required: true,
  },
  {
    name: "customer_payment_mode",
    label: "Customer Payment Mode",
    type: "payment_method_select",
    required: false,
  },
  {
    name: "customer_payment_remarks",
    label: "Customer Payment Remarks",
    type: "textarea",
    required: false,
  },
  {
    name: "ac_hp_format",
    label: "A/C or H/P Format",
    type: "ac_hp_toggle",
    required: false,
  },
]

export const LOAN_RECEIVER_FIELD_CONFIG = [
  {
    name: "include_handover_block",
    label: "Include Handover & Attested Block",
    type: "boolean_toggle",
    required: false,
    description:
      "Adds the handover paragraph + attested signature footer to the Payorder Letter.",
  },
  {
    name: "payorder_receiver_name",
    label: "Payorder Receiver",
    type: "employee_creatable_select",
    required: false,
    description:
      "Leave blank to print dotted placeholder so the receiver name can be hand-written at the counter.",
  },
]

export const LOAN_CUSTOMER_FIELD_CONFIG = [
  {
    name: "customer_name",
    label: "Customer Name",
    type: "text",
    required: true,
  },
  {
    name: "customer_address",
    label: "Customer Address",
    type: "textarea",
    required: true,
  },
]

export const LOAN_BANK_ACCOUNT_FIELD_CONFIG = [
  {
    name: "bank_account_id",
    label: "Select Bank Account",
    type: "bank_account_select",
    required: false,
  },
  {
    name: "payorder_account_name",
    label: "Account Name",
    type: "text",
    required: false,
  },
  {
    name: "payorder_account_number",
    label: "Account Number",
    type: "text",
    required: false,
  },
  {
    name: "payorder_routing_number",
    label: "Routing Number",
    type: "text",
    required: false,
  },
  {
    name: "payorder_bank_name",
    label: "Bank Name",
    type: "text",
    required: false,
  },
  {
    name: "payorder_branch_name",
    label: "Branch Name",
    type: "text",
    required: false,
  },
]

const ALL_LOAN_FIELDS = [
  ...LOAN_FIELD_CONFIG,
  ...LOAN_CUSTOMER_FIELD_CONFIG,
  ...LOAN_BANK_ACCOUNT_FIELD_CONFIG,
  ...LOAN_RECEIVER_FIELD_CONFIG,
]

export const createEmptyLoanErrors = () =>
  ALL_LOAN_FIELDS.reduce((acc, field) => {
    acc[field.name] = ""
    return acc
  }, {})

export const getLoanTotals = loanInformation => {
  const unitPrice = Number(loanInformation?.unit_price || 0)
  const bankPaid = Number(loanInformation?.bank_paid || 0)
  const customerPaid = Number(loanInformation?.customer_paid || 0)
  const totalHandled = bankPaid + customerPaid
  const difference = unitPrice - totalHandled

  return {
    unitPrice,
    bankPaid,
    customerPaid,
    totalHandled,
    difference,
  }
}

export const formatLoanWords = amount => {
  const numericAmount = Math.max(0, Math.round(Number(amount) || 0))
  return `${convertToWords(numericAmount)} Taka Only`
}

export const validateLoanFormValues = values => {
  const nextErrors = createEmptyLoanErrors()

  ALL_LOAN_FIELDS.forEach(field => {
    const rawValue = values?.[field.name]

    if (
      field.type === "ac_hp_toggle" ||
      field.type === "bank_account_select" ||
      field.type === "payment_method_select" ||
      field.type === "employee_creatable_select" ||
      field.type === "boolean_toggle"
    ) {
      return
    }

    if (field.type === "text" || field.type === "textarea") {
      if (field.required && !String(rawValue ?? "").trim()) {
        nextErrors[field.name] = "This field is required."
      }
      return
    }

    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      nextErrors[field.name] = "This field is required."
      return
    }

    const numericValue = Number(rawValue)
    if (Number.isNaN(numericValue) || numericValue < 0) {
      nextErrors[field.name] = "Enter a valid non-negative amount."
    }
  })

  return nextErrors
}

export const normalizeLoanPayload = values => ({
  bank_name: String(values?.bank_name || "").trim(),
  branch_name: String(values?.branch_name || "").trim(),
  unit_price: Math.max(0, Math.round(Number(values?.unit_price || 0))),
  bank_paid: Math.max(0, Math.round(Number(values?.bank_paid || 0))),
  customer_paid: Math.max(0, Math.round(Number(values?.customer_paid || 0))),
  customer_name: String(values?.customer_name || "").trim(),
  customer_address: String(values?.customer_address || "").trim(),
  ac_hp_format: AC_HP_FORMAT_OPTIONS.includes(values?.ac_hp_format)
    ? values.ac_hp_format
    : DEFAULT_AC_HP_FORMAT,
  bank_account: {
    id: values?.bank_account_id ? Number(values.bank_account_id) : null,
    label: String(values?.payorder_label || "").trim(),
    account_name: String(values?.payorder_account_name || "").trim(),
    account_number: String(values?.payorder_account_number || "").trim(),
    routing_number: String(values?.payorder_routing_number || "").trim(),
    bank_name: String(values?.payorder_bank_name || "").trim(),
    branch_name: String(values?.payorder_branch_name || "").trim(),
  },
  customer_payment_mode: String(values?.customer_payment_mode || "").trim(),
  bank_payment_mode: String(values?.bank_payment_mode || "").trim(),
  customer_payment_remarks: String(values?.customer_payment_remarks || "").trim(),
  bank_payment_remarks: String(values?.bank_payment_remarks || "").trim(),
  payorder_receiver_name: String(values?.payorder_receiver_name || "").trim(),
  include_handover_block: Boolean(values?.include_handover_block),
})

export const LoanInformationSummary = ({
  loanInformation,
  className = "mb-4",
}) => {
  const totals = getLoanTotals(loanInformation)

  return (
    <div className={`money-receipt-payment-banner ${className}`.trim()}>
      <div>
        <small className="text-muted d-block">Unit Price</small>
        <span className="fw-semibold">{formatBDT(totals.unitPrice)}</span>
      </div>
      <div>
        <small className="text-muted d-block">Bank Paid</small>
        <span className="fw-semibold text-success">
          {formatBDT(totals.bankPaid)}
        </span>
      </div>
      <div className="money-receipt-payment-banner__words">
        <small className="text-muted d-block">Customer Paid</small>
        <span className="fw-semibold text-primary">
          {formatBDT(totals.customerPaid)}
        </span>
      </div>
    </div>
  )
}

export const LoanInformationMetrics = ({ loanInformation, className = "" }) => {
  const totals = getLoanTotals(loanInformation)

  return (
    <div className={`money-receipt-loan-summary ${className}`.trim()}>
      <div className="money-receipt-loan-summary__item">
        <small className="text-muted d-block">Total Loan Handling</small>
        <span className="fw-semibold">{formatBDT(totals.totalHandled)}</span>
      </div>
      <div className="money-receipt-loan-summary__item">
        <small className="text-muted d-block">Difference vs Unit Price</small>
        <span className="fw-semibold">{formatBDT(totals.difference)}</span>
      </div>
      <div className="money-receipt-loan-summary__item">
        <small className="text-muted d-block">In Words</small>
        <span className="fw-semibold">
          {formatLoanWords(totals.totalHandled)}
        </span>
      </div>
      <div className="money-receipt-loan-summary__item">
        <small className="text-muted d-block">Loan Record Status</small>
        <span className="fw-semibold">
          {totals.totalHandled > 0 ? "Recorded" : "Not Recorded"}
        </span>
      </div>
    </div>
  )
}

const LoanDetailsCard = ({ title, rows }) => (
  <div className="rounded-3 border bg-white p-3 mb-3">
    <div className="text-uppercase text-muted small fw-semibold mb-3">
      {title}
    </div>
    <div className="specs-list">
      {rows.map(item => (
        <div
          key={item.label}
          className="d-flex justify-content-between py-2 border-bottom gap-3"
        >
          <span className="text-muted">{item.label}:</span>
          <span className="fw-medium text-end">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
)

export const PayorderReceiverCard = ({ loanInformation }) => {
  const includeHandover = Boolean(loanInformation?.include_handover_block)
  const receiverName = loanInformation?.payorder_receiver_name
  const hasReceiver = includeHandover || receiverName

  if (!hasReceiver) {
    return (
      <div className="rounded-3 border bg-white p-3 mb-3">
        <div className="text-uppercase text-muted small fw-semibold mb-3">
          Payorder Receiver
        </div>
        <div className="text-muted small">No receiver information set</div>
      </div>
    )
  }

  const rows = [
    {
      label: "Handover & Attested Block",
      value: includeHandover ? "Yes" : "No",
    },
    {
      label: "Payorder Receiver",
      value: receiverName || "Blank — to be filled by hand",
    },
  ]

  return (
    <div className="rounded-3 border bg-white p-3 mb-3">
      <div className="text-uppercase text-muted small fw-semibold mb-3">
        Payorder Receiver
      </div>
      <div className="specs-list">
        {rows.map(item => (
          <div
            key={item.label}
            className="d-flex justify-content-between py-2 border-bottom gap-3"
          >
            <span className="text-muted">{item.label}:</span>
            <span className="fw-medium text-end">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export const LoanInformationDetails = ({ loanInformation }) => {
  const totals = getLoanTotals(loanInformation)
  const bankAccount = loanInformation?.bank_account || {}
  const payorder = {
    account_name:
      bankAccount.account_name || loanInformation?.payorder_account_name || "",
    account_number:
      bankAccount.account_number ||
      loanInformation?.payorder_account_number ||
      "",
    routing_number:
      bankAccount.routing_number ||
      loanInformation?.payorder_routing_number ||
      "",
    bank_name:
      bankAccount.bank_name || loanInformation?.payorder_bank_name || "",
    branch_name:
      bankAccount.branch_name || loanInformation?.payorder_branch_name || "",
  }
  const hasBankAccount =
    bankAccount?.id ||
    loanInformation?.bank_account_id ||
    payorder.account_name ||
    payorder.account_number

  const customerRows = [
    { label: "Customer Name", value: loanInformation?.customer_name || "N/A" },
    {
      label: "Customer Address",
      value: loanInformation?.customer_address || "N/A",
    },
  ]

  const loanRows = [
    { label: "Bank Name", value: loanInformation?.bank_name || "N/A" },
    {
      label: "Branch Name/Address",
      value: loanInformation?.branch_name || "N/A",
    },
    { label: "Unit Price", value: formatBDT(totals.unitPrice) },
    { label: "Bank Paid", value: formatBDT(totals.bankPaid) },
    {
      label: "Bank Payment Mode",
      value: loanInformation?.bank_payment_mode || "Pay Order",
    },
    {
      label: "Bank Payment Remarks",
      value: loanInformation?.bank_payment_remarks || "N/A",
    },
    { label: "Customer Paid", value: formatBDT(totals.customerPaid) },
    {
      label: "Customer Payment Mode",
      value: loanInformation?.customer_payment_mode || "Cash",
    },
    {
      label: "Customer Payment Remarks",
      value: loanInformation?.customer_payment_remarks || "N/A",
    },
    {
      label: "A/C or H/P Format",
      value: loanInformation?.ac_hp_format || DEFAULT_AC_HP_FORMAT,
    },
    { label: "Total Loan Handling", value: formatBDT(totals.totalHandled) },
    { label: "Difference vs Unit Price", value: formatBDT(totals.difference) },
    { label: "In Words", value: formatLoanWords(totals.totalHandled) },
  ]

  const bankAccountRows = [
    { label: "Payorder Account Name", value: payorder.account_name || "N/A" },
    { label: "Payorder Account Number", value: payorder.account_number || "N/A" },
    { label: "Payorder Routing Number", value: payorder.routing_number || "N/A" },
    { label: "Payorder Bank Name", value: payorder.bank_name || "N/A" },
    { label: "Payorder Branch Name", value: payorder.branch_name || "N/A" },
  ]

  return (
    <>
      <LoanDetailsCard title="Customer Information" rows={customerRows} />
      <LoanDetailsCard title="Loan Details" rows={loanRows} />
      {hasBankAccount && (
        <LoanDetailsCard
          title="Payorder Letter Bank Account"
          rows={bankAccountRows}
        />
      )}
    </>
  )
}

export const buildLoanFormatPreviewRows = loanInformation => {
  const format = AC_HP_FORMAT_OPTIONS.includes(loanInformation?.ac_hp_format)
    ? loanInformation.ac_hp_format
    : DEFAULT_AC_HP_FORMAT
  const customerName = String(loanInformation?.customer_name || "").trim()
  const customerAddress = String(loanInformation?.customer_address || "").trim()
  const bankName = String(loanInformation?.bank_name || "").trim()
  const branchName = String(loanInformation?.branch_name || "").trim()
  const address = branchName || customerAddress

  if (format === "A/C") {
    return {
      format,
      rows: [
        { label: "Name", value: bankName || "—" },
        { label: "A/C", value: customerName || "—" },
        { label: "Address", value: address || "—" },
      ],
    }
  }

  const composedName = bankName
    ? `${customerName || "—"} H/P ${bankName}`
    : customerName || "—"

  return {
    format,
    rows: [
      { label: "Name", value: composedName },
      { label: "Address", value: address || "—" },
    ],
  }
}

export const LoanFormatPreview = ({ loanInformation, className = "" }) => {
  const { format, rows } = buildLoanFormatPreviewRows(loanInformation)

  return (
    <div className={`rounded-3 border bg-light-subtle p-3 ${className}`.trim()}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-uppercase text-muted small fw-semibold">
          PDF Customer/Bank Preview
        </span>
        <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
          {format}
        </span>
      </div>
      <div className="specs-list">
        {rows.map(item => (
          <div
            key={item.label}
            className="d-flex justify-content-between py-2 border-bottom gap-3"
          >
            <span className="text-muted">{item.label}:</span>
            <span className="fw-medium text-end">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="text-muted small mt-2">
        Used in Bank Delivery Challan and Bank Bill PDFs.
      </div>
    </div>
  )
}

const LoanFieldInput = ({
  field,
  value,
  onChange,
  invalid,
  disabled,
  bankAccounts = [],
  paymentMethods = [],
  employees = [],
}) => {
  if (field.type === "textarea") {
    return (
      <Input
        type="textarea"
        rows={2}
        name={field.name}
        value={value ?? ""}
        onChange={onChange}
        invalid={invalid}
        disabled={disabled}
      />
    )
  }

  if (field.type === "boolean_toggle") {
    const current = Boolean(value)
    return (
      <ButtonGroup size="sm" className="d-flex">
        <Button
          type="button"
          color={current === true ? "primary" : "light"}
          outline={current !== true}
          onClick={() =>
            onChange({ target: { name: field.name, value: true } })
          }
          disabled={disabled}
        >
          Yes
        </Button>
        <Button
          type="button"
          color={current === false ? "primary" : "light"}
          outline={current !== false}
          onClick={() =>
            onChange({ target: { name: field.name, value: false } })
          }
          disabled={disabled}
        >
          No
        </Button>
      </ButtonGroup>
    )
  }

  if (field.type === "ac_hp_toggle") {
    const current = AC_HP_FORMAT_OPTIONS.includes(value)
      ? value
      : DEFAULT_AC_HP_FORMAT
    return (
      <ButtonGroup size="sm" className="d-flex">
        {AC_HP_FORMAT_OPTIONS.map(option => (
          <Button
            key={option}
            type="button"
            color={current === option ? "primary" : "light"}
            outline={current !== option}
            onClick={() =>
              onChange({ target: { name: field.name, value: option } })
            }
            disabled={disabled}
          >
            {option}
          </Button>
        ))}
      </ButtonGroup>
    )
  }

  if (field.type === "bank_account_select") {
    return (
      <Input
        type="select"
        name={field.name}
        value={value ?? ""}
        onChange={onChange}
        invalid={invalid}
        disabled={disabled}
      >
        <option value="">Select bank account</option>
        {(bankAccounts || []).map(account => (
          <option key={account.id} value={account.id}>
            {[account.label, account.bank_name, account.account_number]
              .filter(Boolean)
              .join(" - ")}
            {account.is_default ? " (default)" : ""}
          </option>
        ))}
      </Input>
    )
  }

  if (field.type === "payment_method_select") {
    return (
      <Input
        type="select"
        name={field.name}
        value={value ?? ""}
        onChange={onChange}
        invalid={invalid}
        disabled={disabled}
      >
        <option value="">Select payment mode</option>
        {(paymentMethods || []).map(method => (
          <option key={method.id ?? method.name} value={method.name}>
            {method.name}
          </option>
        ))}
      </Input>
    )
  }

  if (field.type === "employee_creatable_select") {
    const options = (employees || []).map(emp => ({
      value: emp.name,
      label: emp.name,
    }))
    const stringValue = value ?? ""
    const selectedOption = stringValue
      ? options.find(opt => opt.value === stringValue) || {
          value: stringValue,
          label: stringValue,
        }
      : null

    return (
      <CreatableSelect
        isClearable
        isDisabled={disabled}
        options={options}
        value={selectedOption}
        placeholder="Select or type receiver name"
        onChange={option =>
          onChange({
            target: { name: field.name, value: option ? option.value : "" },
          })
        }
        onCreateOption={inputValue =>
          onChange({
            target: { name: field.name, value: inputValue.trim() },
          })
        }
        formatCreateLabel={inputValue => `Use "${inputValue}"`}
        classNamePrefix="react-select"
        className={invalid ? "is-invalid" : ""}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={{
          menu: base => ({
            ...base,
            backgroundColor: "#fff",
            border: "1px solid #dee2e6",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            zIndex: 1060,
          }),
          menuPortal: base => ({ ...base, zIndex: 1060 }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? "#0d6efd"
              : state.isFocused
                ? "#f1f3f5"
                : "#fff",
            color: state.isSelected ? "#fff" : "#212529",
            cursor: "pointer",
          }),
        }}
      />
    )
  }

  return (
    <Input
      type={field.type}
      min={field.type === "number" ? "0" : undefined}
      name={field.name}
      value={value ?? ""}
      onChange={onChange}
      invalid={invalid}
      disabled={disabled}
    />
  )
}

export const LoanInformationFormFields = ({
  values,
  errors,
  onChange,
  disabled = false,
  bankAccounts = [],
  paymentMethods = [],
  employees = [],
  showLoanSections = true,
}) => (
  <>
    <div className="rounded-3 border bg-white p-3 mb-3">
      <div className="text-uppercase text-muted small fw-semibold mb-3">
        Customer Information
      </div>
      <Row className="g-3">
        {LOAN_CUSTOMER_FIELD_CONFIG.map(field => (
          <Col md={field.type === "textarea" ? "12" : "6"} key={field.name}>
            <Label className="form-label">{field.label}</Label>
            <LoanFieldInput
              field={field}
              value={values?.[field.name]}
              onChange={onChange}
              invalid={Boolean(errors?.[field.name])}
              disabled={disabled}
              bankAccounts={bankAccounts}
              paymentMethods={paymentMethods}
              employees={employees}
            />
            <FormFeedback>{errors?.[field.name]}</FormFeedback>
          </Col>
        ))}
      </Row>
    </div>

    {showLoanSections && (
      <>
        <div className="rounded-3 border bg-white p-3 mb-3">
          <div className="text-uppercase text-muted small fw-semibold mb-3">
            Loan Details
          </div>
          <Row className="g-3">
            {LOAN_FIELD_CONFIG.map(field => (
              <Col md={field.type === "textarea" ? "12" : "6"} key={field.name}>
                <Label className="form-label">{field.label}</Label>
                <LoanFieldInput
                  field={field}
                  value={values?.[field.name]}
                  onChange={onChange}
                  invalid={Boolean(errors?.[field.name])}
                  disabled={disabled}
                  bankAccounts={bankAccounts}
                  paymentMethods={paymentMethods}
                  employees={employees}
                />
                <FormFeedback>{errors?.[field.name]}</FormFeedback>
              </Col>
            ))}
          </Row>
        </div>

        <div className="rounded-3 border bg-white p-3 mb-3">
          <div className="text-uppercase text-muted small fw-semibold mb-3">
            Payorder Letter Bank Account
          </div>
          <Row className="g-3">
            {LOAN_BANK_ACCOUNT_FIELD_CONFIG.map(field => (
              <Col md="6" key={field.name}>
                <Label className="form-label">{field.label}</Label>
                <LoanFieldInput
                  field={field}
                  value={values?.[field.name]}
                  onChange={onChange}
                  invalid={Boolean(errors?.[field.name])}
                  disabled={disabled}
                  bankAccounts={bankAccounts}
                  paymentMethods={paymentMethods}
                  employees={employees}
                />
                <FormFeedback>{errors?.[field.name]}</FormFeedback>
              </Col>
            ))}
          </Row>
        </div>

        <div className="rounded-3 border bg-white p-3">
          <div className="text-uppercase text-muted small fw-semibold mb-3">
            Payorder Receiver
          </div>
          <Row className="g-3">
            {LOAN_RECEIVER_FIELD_CONFIG.map(field => (
              <Col md="12" key={field.name}>
                <Label className="form-label">{field.label}</Label>
                <LoanFieldInput
                  field={field}
                  value={values?.[field.name]}
                  onChange={onChange}
                  invalid={Boolean(errors?.[field.name])}
                  disabled={disabled}
                  bankAccounts={bankAccounts}
                  paymentMethods={paymentMethods}
                  employees={employees}
                />
                {field.description ? (
                  <small className="text-muted d-block mt-1">
                    {field.description}
                  </small>
                ) : null}
                <FormFeedback>{errors?.[field.name]}</FormFeedback>
              </Col>
            ))}
          </Row>
        </div>
      </>
    )}
  </>
)
